/**
 * Lightweight, privacy-respecting client-side behavior/interaction heuristic collector
 *
 * This module collects summary statistics about user interactions to generate a
 * client-side risk score. It does NOT record:
 * - Actual keystrokes or input values
 * - Exact mouse coordinates
 * - Any personally identifiable information
 *
 * Usage:
 *   import { initBehaviorSignal, mark, getBehaviorSignal, stop } from './behaviorSignal';
 *
 *   initBehaviorSignal({ debug: false });
 *   // ... user interacts with page ...
 *   mark('submit_clicked');
 *   const signal = getBehaviorSignal();
 *   // Send signal to backend
 */

class WelfordStats {
  constructor() {
    this.count = 0;
    this.mean = 0;
    this.m2 = 0;
    this.min = Infinity;
    this.max = -Infinity;
  }

  add(value) {
    this.count++;
    const delta = value - this.mean;
    this.mean += delta / this.count;
    const delta2 = value - this.mean;
    this.m2 += delta * delta2;
    this.min = Math.min(this.min, value);
    this.max = Math.max(this.max, value);
  }

  getVariance() {
    return this.count < 2 ? 0 : this.m2 / this.count;
  }

  getStd() {
    return Math.sqrt(this.getVariance());
  }

  getMean() {
    return this.mean;
  }

  getMin() {
    return this.count === 0 ? 0 : this.min;
  }

  getMax() {
    return this.count === 0 ? 0 : this.max;
  }

  getCount() {
    return this.count;
  }
}

class BehaviorSignalCollector {
  constructor(options = {}) {
    this.options = {
      debug: false,
      throttleMs: 100,
      maxPayloadSizeBytes: 8192,
      enabled: true,
      ...options
    };

    this.initTs = Date.now();
    this.marks = {};
    this.lastKeyTs = null;
    this.lastPointerTs = null;
    this.lastScrollTs = null;
    this.lastPointerX = null;
    this.lastPointerY = null;
    this.lastScrollY = null;
    this.lastScrollTs = null;

    // Stats collectors
    this.keyIntervalStats = new WelfordStats();

    // Counters
    this.keydownCount = 0;
    this.pasteCount = 0;
    this.backspaceCount = 0;
    this.pointerMoveCount = 0;
    this.pointerDirectionChanges = 0;
    this.pointerClickCount = 0;
    this.pointerDownCount = 0;
    this.pointerUpCount = 0;
    this.scrollEventCount = 0;
    this.scrollDeltaTotal = 0;
    this.maxScrollSpeed = 0;
    this.visibilityChangeCount = 0;
    this.blurCount = 0;

    // First interaction timestamps
    this.firstInteractionTs = null;
    this.firstKeydownTs = null;
    this.firstPointerTs = null;
    this.firstScrollTs = null;

    // Pointer movement tracking
    this.lastPointerDx = 0;
    this.lastPointerDy = 0;

    // Throttle tracking
    this.lastPointerMoveProcessedTs = 0;
    this.lastScrollProcessedTs = 0;

    // Listeners
    this.listeners = [];

    // Debug interval
    this.debugInterval = null;

    if (!this.options.enabled) {
      return;
    }

    this.attachListeners();

    if (this.options.debug) {
      this.debugInterval = setInterval(() => {
        console.log('[BehaviorSignal Debug]', this.getSnapshot());
      }, 5000);
    }
  }

  attachListeners() {
    // Keyboard events
    const keydownListener = (e) => this.handleKeydown(e);
    const pasteListener = () => this.handlePaste();

    // Pointer events
    const pointerMoveListener = (e) => this.handlePointerMove(e);
    const pointerDownListener = () => this.handlePointerDown();
    const pointerUpListener = () => this.handlePointerUp();
    const clickListener = () => this.handleClick();

    // Scroll events
    const scrollListener = (e) => this.handleScroll(e);

    // Focus/visibility events
    const visibilityChangeListener = () => this.handleVisibilityChange();
    const blurListener = () => this.handleBlur();

    document.addEventListener('keydown', keydownListener);
    document.addEventListener('paste', pasteListener);
    document.addEventListener('pointermove', pointerMoveListener, { passive: true });
    document.addEventListener('pointerdown', pointerDownListener);
    document.addEventListener('pointerup', pointerUpListener);
    document.addEventListener('click', clickListener);
    document.addEventListener('scroll', scrollListener, { passive: true });
    document.addEventListener('visibilitychange', visibilityChangeListener);
    window.addEventListener('blur', blurListener);

    this.listeners = [
      { target: document, type: 'keydown', handler: keydownListener },
      { target: document, type: 'paste', handler: pasteListener },
      { target: document, type: 'pointermove', handler: pointerMoveListener },
      { target: document, type: 'pointerdown', handler: pointerDownListener },
      { target: document, type: 'pointerup', handler: pointerUpListener },
      { target: document, type: 'click', handler: clickListener },
      { target: document, type: 'scroll', handler: scrollListener },
      { target: document, type: 'visibilitychange', handler: visibilityChangeListener },
      { target: window, type: 'blur', handler: blurListener },
    ];
  }

  handleKeydown(e) {
    if (!this.firstInteractionTs) {
      this.firstInteractionTs = Date.now();
    }
    if (!this.firstKeydownTs) {
      this.firstKeydownTs = Date.now();
    }

    // Skip modifier-only keys
    if (['Shift', 'Control', 'Alt', 'Meta'].includes(e.key)) {
      return;
    }

    this.keydownCount++;

    if (e.key === 'Backspace' || e.key === 'Delete') {
      this.backspaceCount++;
    }

    const now = Date.now();
    if (this.lastKeyTs !== null) {
      const interval = now - this.lastKeyTs;
      this.keyIntervalStats.add(interval);
    }
    this.lastKeyTs = now;
  }

  handlePaste() {
    if (!this.firstInteractionTs) {
      this.firstInteractionTs = Date.now();
    }
    this.pasteCount++;
  }

  handlePointerMove(e) {
    const now = Date.now();

    // Throttle processing
    if (now - this.lastPointerMoveProcessedTs < this.options.throttleMs) {
      return;
    }
    this.lastPointerMoveProcessedTs = now;

    if (!this.firstInteractionTs) {
      this.firstInteractionTs = now;
    }
    if (!this.firstPointerTs) {
      this.firstPointerTs = now;
    }

    this.pointerMoveCount++;

    // Track direction changes (coarse, privacy-preserving)
    if (this.lastPointerX !== null && this.lastPointerY !== null) {
      const dx = e.clientX - this.lastPointerX;
      const dy = e.clientY - this.lastPointerY;

      // Only track sign changes (not actual coordinates)
      const dxSign = Math.sign(dx);
      const dySign = Math.sign(dy);
      const prevDxSign = Math.sign(this.lastPointerDx);
      const prevDySign = Math.sign(this.lastPointerDy);

      if ((dxSign !== 0 && prevDxSign !== 0 && dxSign !== prevDxSign) ||
          (dySign !== 0 && prevDySign !== 0 && dySign !== prevDySign)) {
        this.pointerDirectionChanges++;
      }

      this.lastPointerDx = dx;
      this.lastPointerDy = dy;
    }

    this.lastPointerX = e.clientX;
    this.lastPointerY = e.clientY;
  }

  handlePointerDown() {
    if (!this.firstInteractionTs) {
      this.firstInteractionTs = Date.now();
    }
    if (!this.firstPointerTs) {
      this.firstPointerTs = Date.now();
    }
    this.pointerDownCount++;
  }

  handlePointerUp() {
    if (!this.firstInteractionTs) {
      this.firstInteractionTs = Date.now();
    }
    this.pointerUpCount++;
  }

  handleClick() {
    if (!this.firstInteractionTs) {
      this.firstInteractionTs = Date.now();
    }
    this.pointerClickCount++;
  }

  handleScroll(e) {
    const now = Date.now();

    // Throttle processing
    if (now - this.lastScrollProcessedTs < this.options.throttleMs) {
      return;
    }
    this.lastScrollProcessedTs = now;

    if (!this.firstInteractionTs) {
      this.firstInteractionTs = now;
    }
    if (!this.firstScrollTs) {
      this.firstScrollTs = now;
    }

    this.scrollEventCount++;

    const currentScrollY = window.scrollY || window.pageYOffset;

    if (this.lastScrollY !== null) {
      const delta = Math.abs(currentScrollY - this.lastScrollY);
      this.scrollDeltaTotal += delta;

      // Calculate scroll speed
      if (this.lastScrollTs !== null) {
        const timeDelta = now - this.lastScrollTs;
        if (timeDelta > 0) {
          const speed = delta / timeDelta;
          this.maxScrollSpeed = Math.max(this.maxScrollSpeed, speed);
        }
      }
    }

    this.lastScrollY = currentScrollY;
    this.lastScrollTs = now;
  }

  handleVisibilityChange() {
    this.visibilityChangeCount++;
  }

  handleBlur() {
    this.blurCount++;
  }

  mark(eventName) {
    this.marks[eventName] = Date.now();
  }

  getSnapshot(nonce = null) {
    const now = Date.now();
    const sessionDurationMs = now - this.initTs;

    const timeToFirstInteractionMs = this.firstInteractionTs
      ? this.firstInteractionTs - this.initTs
      : null;

    const timeToFirstKeydownMs = this.firstKeydownTs
      ? this.firstKeydownTs - this.initTs
      : null;

    const timeToFirstPointerMs = this.firstPointerTs
      ? this.firstPointerTs - this.initTs
      : null;

    const timeToFirstScrollMs = this.firstScrollTs
      ? this.firstScrollTs - this.initTs
      : null;

    const submitTs = this.marks.submit_clicked || this.marks.submit || this.marks.form_submit;
    const timeFromFirstInteractionToSubmitMs = (submitTs && this.firstInteractionTs)
      ? submitTs - this.firstInteractionTs
      : null;

    // Check if we're on mobile
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );

    const signal = {
      version: 'behavior-signal/v1',
      ts: now,
      nonce,
      page: {
        path: typeof window !== 'undefined' ? window.location.pathname : '',
        referrer: typeof document !== 'undefined' ? document.referrer : '',
        isMobile
      },
      timing: {
        sessionDurationMs,
        timeToFirstInteractionMs,
        timeToFirstKeydownMs,
        timeToFirstPointerMs,
        timeToFirstScrollMs,
        timeFromFirstInteractionToSubmitMs
      },
      keyboard: {
        keydownCount: this.keydownCount,
        pasteCount: this.pasteCount,
        backspaceCount: this.backspaceCount,
        avgKeyIntervalMs: this.keyIntervalStats.getCount() > 0
          ? Math.round(this.keyIntervalStats.getMean())
          : null,
        stdKeyIntervalMs: this.keyIntervalStats.getCount() > 0
          ? Math.round(this.keyIntervalStats.getStd())
          : null,
        minKeyIntervalMs: this.keyIntervalStats.getCount() > 0
          ? Math.round(this.keyIntervalStats.getMin())
          : null,
        maxKeyIntervalMs: this.keyIntervalStats.getCount() > 0
          ? Math.round(this.keyIntervalStats.getMax())
          : null
      },
      pointer: {
        moveCount: this.pointerMoveCount,
        directionChangeCount: this.pointerDirectionChanges,
        clickCount: this.pointerClickCount,
        downCount: this.pointerDownCount,
        upCount: this.pointerUpCount
      },
      scroll: {
        eventCount: this.scrollEventCount,
        totalDeltaMagnitude: Math.round(this.scrollDeltaTotal),
        maxScrollSpeed: Math.round(this.maxScrollSpeed * 1000) / 1000
      },
      focus: {
        visibilityChangeCount: this.visibilityChangeCount,
        blurCount: this.blurCount
      },
      env: {
        isWebdriver: navigator.webdriver === true,
        deviceMemory: navigator.deviceMemory || null,
        hardwareConcurrency: navigator.hardwareConcurrency || null,
        prefersReducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches
      },
      derived: this.computeDerivedMetrics({
        timeToFirstInteractionMs,
        timeFromFirstInteractionToSubmitMs,
        submitTs
      })
    };

    return signal;
  }

  computeDerivedMetrics({ timeToFirstInteractionMs, timeFromFirstInteractionToSubmitMs, submitTs }) {
    let score = 0;
    const flags = [];

    // Rule 1: navigator.webdriver detected
    if (navigator.webdriver === true) {
      score += 30;
      flags.push('webdriver_detected');
    }

    // Rule 2: Submit < 800ms after first interaction
    if (submitTs && timeFromFirstInteractionToSubmitMs !== null && timeFromFirstInteractionToSubmitMs < 800) {
      score += 25;
      flags.push('instant_submit');
    }

    // Rule 3: Extremely regular key intervals
    const keyStd = this.keyIntervalStats.getStd();
    const keyCount = this.keyIntervalStats.getCount();
    if (keyStd < 5 && keyCount > 10) {
      score += 20;
      flags.push('extreme_key_regularity');
    }

    // Rule 4: Zero pointer + zero scroll + high key count
    if (this.pointerMoveCount === 0 && this.scrollEventCount === 0 && this.keydownCount > 20) {
      score += 15;
      flags.push('no_pointer_events');
    }

    // Rule 5: Heavy paste + fast submit
    if (this.pasteCount > 3 && timeFromFirstInteractionToSubmitMs !== null && timeFromFirstInteractionToSubmitMs < 2000) {
      score += 10;
      flags.push('heavy_paste_fast_submit');
    }

    // Rule 6: No interactions but submit marked
    if (submitTs && this.firstInteractionTs === null) {
      score += 10;
      flags.push('submit_without_interaction');
    }

    // Reward: Good human variance indicators
    const hasReasonableVariance = keyStd > 20 && keyStd < 500 && keyCount > 5;
    const hasMixedInteractions = this.pointerMoveCount > 0 && this.keydownCount > 0;
    if (hasReasonableVariance || hasMixedInteractions) {
      score = Math.max(0, score - 5);
    }

    // Additional flags for edge cases
    if (this.keydownCount > 100 && this.pointerMoveCount === 0) {
      flags.push('keyboard_only_heavy_usage');
    }

    if (this.pointerClickCount > 50) {
      flags.push('excessive_clicks');
    }

    if (timeToFirstInteractionMs !== null && timeToFirstInteractionMs > 30000) {
      flags.push('delayed_first_interaction');
    }

    // Clamp score to 0-100
    const riskScore = Math.max(0, Math.min(100, score));

    return {
      riskScore,
      flags
    };
  }

  reset() {
    this.initTs = Date.now();
    this.marks = {};
    this.lastKeyTs = null;
    this.lastPointerTs = null;
    this.lastScrollTs = null;
    this.lastPointerX = null;
    this.lastPointerY = null;
    this.lastScrollY = null;
    this.lastScrollTs = null;

    this.keyIntervalStats = new WelfordStats();

    this.keydownCount = 0;
    this.pasteCount = 0;
    this.backspaceCount = 0;
    this.pointerMoveCount = 0;
    this.pointerDirectionChanges = 0;
    this.pointerClickCount = 0;
    this.pointerDownCount = 0;
    this.pointerUpCount = 0;
    this.scrollEventCount = 0;
    this.scrollDeltaTotal = 0;
    this.maxScrollSpeed = 0;
    this.visibilityChangeCount = 0;
    this.blurCount = 0;

    this.firstInteractionTs = null;
    this.firstKeydownTs = null;
    this.firstPointerTs = null;
    this.firstScrollTs = null;

    this.lastPointerDx = 0;
    this.lastPointerDy = 0;
    this.lastPointerMoveProcessedTs = 0;
    this.lastScrollProcessedTs = 0;
  }

  stop() {
    this.listeners.forEach(({ target, type, handler }) => {
      target.removeEventListener(type, handler);
    });
    this.listeners = [];

    if (this.debugInterval) {
      clearInterval(this.debugInterval);
      this.debugInterval = null;
    }
  }
}

// Singleton instance
let globalCollector = null;

/**
 * Initialize the behavior signal collector
 * @param {Object} options - Configuration options
 * @param {boolean} options.debug - Enable debug logging
 * @param {number} options.throttleMs - Throttle interval for pointer/scroll events
 * @param {number} options.maxPayloadSizeBytes - Maximum payload size
 * @param {boolean} options.enabled - Enable/disable collection
 * @returns {BehaviorSignalCollector} The collector instance
 */
export function initBehaviorSignal(options = {}) {
  if (globalCollector) {
    globalCollector.stop();
  }
  globalCollector = new BehaviorSignalCollector(options);
  return globalCollector;
}

/**
 * Mark a specific event timestamp (e.g., 'submit_clicked', 'page_load', 'first_input')
 * @param {string} eventName - Name of the event to mark
 */
export function mark(eventName) {
  if (!globalCollector) {
    console.warn('[BehaviorSignal] Collector not initialized. Call initBehaviorSignal() first.');
    return;
  }
  globalCollector.mark(eventName);
}

/**
 * Get the current behavior signal payload
 * @param {string} nonce - Optional nonce for attestation
 * @returns {Object} The behavior signal payload
 */
export function getBehaviorSignal(nonce = null) {
  if (!globalCollector) {
    console.warn('[BehaviorSignal] Collector not initialized. Call initBehaviorSignal() first.');
    return null;
  }

  const signal = globalCollector.getSnapshot(nonce);

  // Check payload size and trim if needed
  const payloadStr = JSON.stringify(signal);
  const payloadBytes = new Blob([payloadStr]).size;

  if (payloadBytes > globalCollector.options.maxPayloadSizeBytes) {
    console.warn(`[BehaviorSignal] Payload size (${payloadBytes} bytes) exceeds maximum (${globalCollector.options.maxPayloadSizeBytes} bytes). Trimming...`);

    // Remove detailed timing info to reduce size
    delete signal.keyboard.minKeyIntervalMs;
    delete signal.keyboard.maxKeyIntervalMs;
    delete signal.pointer.directionChangeCount;
    delete signal.scroll.maxScrollSpeed;
  }

  return signal;
}

/**
 * Reset the collector state
 */
export function reset() {
  if (!globalCollector) {
    console.warn('[BehaviorSignal] Collector not initialized. Call initBehaviorSignal() first.');
    return;
  }
  globalCollector.reset();
}

/**
 * Stop the collector and remove all event listeners
 */
export function stop() {
  if (!globalCollector) {
    return;
  }
  globalCollector.stop();
  globalCollector = null;
}

/**
 * Get the collector instance (for advanced usage)
 * @returns {BehaviorSignalCollector|null}
 */
export function getCollectorInstance() {
  return globalCollector;
}
